import { motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useState } from "react";

const LOGO_SVG = "https://customer-assets.emergentagent.com/job_8393e55a-bcbf-4c72-bdaa-a0f6a593df85/artifacts/r7njs9wp_obsidian_bespoke_experiences_hq.svg";
const HERO_BG = "https://customer-assets.emergentagent.com/job_8393e55a-bcbf-4c72-bdaa-a0f6a593df85/artifacts/b6glp3ry_Adobe%20Express%20-%20file-2.png";

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const NAV_LINKS = [
  { label: "Experiences", href: "#experiences" },
  { label: "All-Inclusive", href: "#all-inclusive" },
  { label: "Bespoke", href: "#bespoke" },
  { label: "Contact", href: "#contact" },
];

export default function HeroSection() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover animate-ken-burns scale-105" />
      </div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black via-black/60 to-black/40" />

      {/* Nav */}
      <nav data-testid="hero-nav" className="relative z-10 flex items-center justify-between px-6 md:px-12 lg:px-16 py-6 md:py-8">
        <img src={LOGO_SVG} alt="Obsidian Bespoke Experiences" className="h-20 md:h-28 w-auto drop-shadow-lg" data-testid="hero-logo" />
        
        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-10 bg-black/30 backdrop-blur-sm px-6 py-2 rounded-full">
          {NAV_LINKS.map(link => (
            <a key={link.label} href={link.href}
               className="font-sans text-white/80 text-xs tracking-[0.2em] uppercase hover:text-[#C5A059] transition-colors duration-500">
              {link.label}
            </a>
          ))}
          <a href="#bespoke" data-testid="nav-book-btn"
             className="font-sans text-xs tracking-[0.2em] uppercase border border-[#C5A059] text-[#C5A059] px-7 py-2.5 hover:bg-[#C5A059] hover:text-black transition-all duration-500">
            Book Now
          </a>
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white z-50" data-testid="mobile-menu-btn">
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8">
          {NAV_LINKS.map(link => (
            <a key={link.label} href={link.href} onClick={() => setMenuOpen(false)}
               className="font-serif text-3xl text-white/80 hover:text-[#C5A059] transition-colors">
              {link.label}
            </a>
          ))}
          <a href="#bespoke" onClick={() => setMenuOpen(false)}
             className="mt-4 font-sans text-xs tracking-[0.2em] uppercase border border-[#C5A059] text-[#C5A059] px-10 py-3">
            Book Now
          </a>
        </motion.div>
      )}

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
        <motion.div variants={stagger} initial="hidden" animate="visible" className="max-w-4xl">
          <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.35em] uppercase mb-8" data-testid="hero-overline">
            El Salvador &middot; Bespoke Experiences
          </motion.p>

          <motion.h1 variants={fadeUp} className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light leading-[0.9] tracking-tight" data-testid="hero-title">
            Discover the
            <br />
            <span className="italic">Extraordinary</span>
          </motion.h1>

          <motion.div variants={fadeUp} className="w-12 h-px bg-[#C5A059] mx-auto my-8" />

          <motion.p variants={fadeUp} className="font-sans text-white/60 text-sm md:text-base font-light max-w-lg mx-auto leading-relaxed" data-testid="hero-subtitle">
            Authentic local knowledge and breathtaking locations
            create unforgettable moments in Central America's
            best-kept secret.
          </motion.p>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5 }}
        className="relative z-10 flex flex-col items-center pb-10 gap-3">
        <span className="font-sans text-white/40 text-[10px] tracking-[0.3em] uppercase">Scroll to discover</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ChevronDown size={20} strokeWidth={1} className="text-white/40" />
        </motion.div>
      </motion.div>
    </section>
  );
}
